import { FC, useEffect, useMemo, type ReactNode } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { useTranslation } from '../contexts/TranslationContext';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';

// Create RTL cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const createAppTheme = (direction: 'rtl' | 'ltr') => createTheme({
  direction,
  typography: {
    fontFamily: "dana, 'Arial'",
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 700,
    },
    body1: {
      fontWeight: 400,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: "dana, 'Arial'",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontFamily: "dana, 'Arial'",
          },
          '& .MuiInputLabel-root': {
            fontFamily: "dana, 'Arial'",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: "dana, 'Arial'",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: "dana, 'Arial'",
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& *': {
            fontFamily: "dana, 'Arial'",
          },
        },
      },
    },
  },
});

const ThemeWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const { currentLanguage } = useTranslation();
  const direction = currentLanguage === 'fa' ? 'rtl' : 'ltr';
  const theme = useMemo(() => createAppTheme(direction), [direction]);

  // Update document direction when language changes
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage, direction]);

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
};

export default ThemeWrapper; 